import type {
  LeaderboardPage,
  LeaderboardsRegions,
} from "lib/types/leaderboards";
import { regionGroups } from "./data";

export const sortData = (data, order: "latest" | "oldest" | undefined) => {
  return data.sort((a, b) => {
    if (order === "oldest") {
      return a.publishTimestamp - b.publishTimestamp;
    } else {
      return b.publishTimestamp - a.publishTimestamp;
    }
  });
};

export const filteredData = (data, filter: string[]) => {
  if (!filter || filter.length === 0) return data;
  if (typeof filter === "object" && filter.length > 0) {
    return data.filter((entry) => filter.includes(entry.region));
  }
  return data.filter((entry) => entry.region === filter);
};

export function findLatestPublishDateId(items) {
  if (items.length === 0) {
    return null;
  }

  let latestItem = items[0];

  for (let i = 1; i < items.length; i++) {
    const currentItem = items[i];

    const currentPublishDate = new Date(currentItem.publishDate);
    const latestPublishDate = new Date(latestItem.publishDate);

    if (currentPublishDate > latestPublishDate) {
      latestItem = currentItem;
    }
  }

  return latestItem.id;
}

export const getGroupedLeaderboards = (
  leaderboards: LeaderboardPage[],
  activeFilters: string[]
) => {
  const selectedRegions = activeFilters.flatMap(
    (filter) => regionGroups[filter]?.regions || []
  );

  const grouped = sortData(
    filteredData(leaderboards, selectedRegions),
    "latest"
  ).reduce((acc, entry) => {
    const year = new Date(entry.publishDate).getFullYear();
    (acc[year] = acc[year] || []).push(entry);
    return acc;
  }, {});

  return Object.entries(grouped).sort(([a], [b]) => Number(b) - Number(a));
};

export const getFilterList = (leaderboards: LeaderboardPage[]) => {
  const availableRegions = new Set(
    leaderboards.map((leaderboard: LeaderboardPage) => leaderboard.region)
  );
  return Object.entries(regionGroups)
    .filter(([_, group]) =>
      group.regions.some((region: LeaderboardsRegions) =>
        availableRegions.has(region)
      )
    )
    .map(([key, group]) => ({ value: key, label: group.label }));
};

type LeaderboardTagPropsType = {
  disabled?: boolean;
  tag?: "Latest Leaderboard" | "Open to application" | "In review";
  tagColor?: "grey" | "black";
  buttonText?: "Apply";
  buttonOnClick?: () => void;
};

export const leaderboardTagProps = (
  leaderboard: LeaderboardPage,
  latestId: string
): LeaderboardTagPropsType => {
  if (leaderboard.publishState === "In review")
    return {
      tagColor: "grey",
      tag: "In review",
      disabled: true,
    };
  if (
    leaderboard.publishState === "Open to application" &&
    leaderboard.applicationLink
  )
    return {
      tag: "Open to application",
      buttonText: "Apply",
      buttonOnClick: () => {
        if (leaderboard.applicationLink) {
          window.open(leaderboard.applicationLink, "_blank");
        }
      },
    };
  if (leaderboard.id === latestId)
    return {
      tagColor: "black",
      tag: "Latest Leaderboard",
    };
  return {};
};

export const REVENUE_FIELDS = new Set(["latestRevenue", "revenueCagr"]);

export const isRevenueField = (field: string): boolean =>
  REVENUE_FIELDS.has(field);

export const withoutRevenueFields = <T extends string>(fields?: T[]): T[] =>
  (fields || []).filter((field) => !REVENUE_FIELDS.has(field));
